import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import StatusBadge from "../../components/common/StatusBadge";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/common/Pagination";
import SearchBar from "../../components/common/Search";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown";
import ActionMenu from "../../components/common/ActionMenu";

import {
  useAdminVendorApprove,
  useAdminVendorReject,
  useAdminVendorsList,
  useAdminVendorSuspend,
} from "../../custom-hooks/queries";

import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineStop,
  AiOutlineUser,
} from "react-icons/ai";
import RejectionModal from "../../components/Modals/RejectionModal";
import { toast } from "react-toastify";

export default function VendorManagement() {
  const navigate = useNavigate();

  // 🔹 Pagination + filters (Explore jaisa)
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;

  // 🔹 Reject / Suspend modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [actionType, setActionType] = useState(null); // "reject" | "suspend"

  // 🔹 Status filter options
  const statusOptions = [
    { id: "pending", name: "Pending" },
    { id: "approved", name: "Approved" },
    { id: "suspended", name: "Suspended" },
    { id: "rejected", name: "Rejected" },
  ];

  // 🔹 Vendors list API
  const { data, isLoading, isError, refetch } = useAdminVendorsList({
    page: currentPage,
    pageSize,
    status: statusFilter,
    search: searchText,
  });

  // 🔹 Mutations
  const approveMutation = useAdminVendorApprove();
  const rejectMutation = useAdminVendorReject();
  const suspendMutation = useAdminVendorSuspend();

  // 🔹 Action menu config
  const vendorActions = (row) => [
    {
      label: "View Vendor Profile",
      icon: <AiOutlineUser />,
      onClick: () => navigate(`/admin/vendors/${row.vendorId}`),
    },

    ...(row?.status?.toLowerCase() !== "approved"
      ? [
          {
            label: "Approve Vendor",
            icon: <AiOutlineCheckCircle />,
            onClick: () =>
              approveMutation.mutate(row.vendorId, {
                onSuccess: () => {
                  toast.success("Vendor approved successfully");
                  refetch(); // Refetch the list to update the status
                },
                onError: () => {
                  toast.error("Failed to approve vendor. Please try again.");
                },
              }),
          },
        ]
      : []),

    {
      label: "Reject Vendor",
      icon: <AiOutlineCloseCircle />,
      danger: true,
      onClick: () => {
        setSelectedVendor(row);
        setActionType("reject");
        setShowRejectModal(true);
      },
    },

    {
      label: "Suspend Vendor",
      icon: <AiOutlineStop />,
      onClick: () => {
        setSelectedVendor(row);
        setActionType("suspend");
        setShowRejectModal(true);
      },
    },
  ];

  // 🔹 Map API response → table data
  const vendorManagement =
    data?.data?.data?.map((item) => ({
      vendorId: item.vendorId,
      userName: item.contactName,
      businessName: item.businessName,
      status: item.status,
      rating: item.rating,
    })) || [];

  // 🔹 Table columns
  const columns = [
    {
      key: "userName",
      label: "VENDOR NAME",
      width: "1.5fr",
      align: "left",
      render: (row) => (
        <div className="font-semibold text-gray-900 truncate">
          {row.userName}
        </div>
      ),
    },
    {
      key: "businessName",
      label: "BUSINESS NAME",
      width: "1.5fr",
      align: "left",
      render: (row) => (
        <div className="text-gray-700 truncate">{row.businessName}</div>
      ),
    },

    {
      key: "status",
      label: "STATUS",
      width: "1fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },

    {
      key: "rating",
      label: "RATING",
      width: "1fr",
      align: "center",
      render: (row) => row.rating ?? "-",
    },
    {
      key: "actions",
      label: "ACTIONS",
      width: "1.2fr",
      align: "center",
      render: (row) => <ActionMenu actions={vendorActions(row)} />,
    },
  ];

  // 🔹 Handlers
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (statusId) => {
    setStatusFilter(statusId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <PageHeader
        title="Vendor Management"
        description="Discover and manage vendors"
      />

      <div className="bg-white rounded-[12px] border border-[#D9D9D9] shadow-sm overflow-hidden mt-4 sm:mt-6">
        {/* 🔍 Search + Filter */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 py-6 sm:py-10 px-4 sm:px-6">
          <SearchBar onSearch={handleSearch} />

          <CustomFilterDropdown
            options={statusOptions}
            onChange={handleStatusChange}
            placeholder="Filter by Status"
            valueKey="id"
            labelKey="name"
          />
        </div>

        {/* 📋 Table */}
        {isError ? (
          <div className="flex justify-center items-center py-20 text-red-500">
            Failed to load vendors. Please try again.
          </div>
        ) : (
          <Table
            columns={columns}
            data={vendorManagement}
            isLoading={isLoading}
            skeletonRows={pageSize}
          />
        )}
      </div>

      {/* 📄 Pagination */}
      {!isLoading && !isError && data?.data?.totalPages > 1 && (
        <div className="mt-4 sm:mt-6">
          <Pagination
            currentPage={data?.data?.currentPage || currentPage}
            totalPages={data?.data?.totalPages || 1}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* ❌ Reject / Suspend Modal */}
      {showRejectModal && selectedVendor && (
        <RejectionModal
          title={actionType === "reject" ? "Reject Vendor" : "Suspend Vendor"}
          description={
            actionType === "reject"
              ? "Please provide a reason for rejecting this vendor."
              : "Please provide a reason for suspending this vendor."
          }
          confirmText={
            actionType === "reject" ? "Reject Vendor" : "Suspend Vendor"
          }
          mutationFn={
            actionType === "reject" ? rejectMutation : suspendMutation
          }
          payload={{
            vendorId: selectedVendor.vendorId,
          }}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedVendor(null);
            setActionType(null);
          }}
          refetch={refetch}
          actionType={actionType}
        />
      )}
    </div>
  );
}
